<template>
  <!--
    Video Block that displays a thumbnail image, title and description.
    When the thumbnail image or "watch now" is clicked an iframe video will expand,
    hiding the description and showing a collapsible transcript.
  -->
  <div
    class="video-block"
    :class="{'video-block--expanded': showVideo}"
  >
    <!-- Thumbnail Image -->
    <button
      v-show="!showVideo"
      title="watch now"
      aria-label="watch now"
      class="video-block__thumbnail"
      @click="toggleVideo"
    >
      <div class="video-block__thumbnail-container">
        <div
          class="video-block__thumbnail-wrapper"
        >
          <img
            class="video-block__thumbnail-image"
            :src="thumbnailUrl"
            :alt="videoTitle"
            aria-label="Watch video"
          >
          <div class="video-block__thumbnail-icon-wrapper">
            <font-awesome-icon
              class="video-block__thumbnail-icon"
              icon="play"
            />
          </div>
        </div>
      </div>
    </button>
    <!-- Video -->
    <div
      v-show="showVideo"
      class="video-block__player"
    >
      <div class="video-block__player-wrapper">
        <pulse-loader
          :color="'#E4E6E2'"
          :size="'20px'"
          :margin="'10px'"
        />
        <iframe
          v-if="showVideo"
          :src="`${videoUrl}?autoplay=1`"
          :title="videoTitle"
          class="video-block__iframe"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        />
      </div>
      <!-- Transcript -->
      <div class="video-block__transcript">
        <button
          :id="`transcript-button-${id}`"
          class="video-block__transcript-button"
          :class="{'video-block__transcript-button--expanded': showTranscript}"
          :aria-controls="`transcript-content-${id}`"
          aria-label="Toggle video transcript"
          :aria-expanded="showTranscript"
          @click="toggleTranscript"
        >
          <span class="video-block__transcript-label">
            <img
              class="video-block__transcript-icon"
              :src="`/themes/custom/education_applications_theme/dist/images/icon-transcript.svg`"
              alt="toggle transcript"
            >
            <img
              class="video-block__transcript-icon-blue"
              :src="`/themes/custom/education_applications_theme/dist/images/icon-transcript-blue.svg`"
              alt="toggle transcript"
            >
            <span>
              Video transcript
            </span>
          </span>
          <img
            class="video-block__transcript-close-btn"
            :src="`/themes/custom/education_applications_theme/dist/images/icon-light-close.svg`"
            alt="toggle transcript"
          >
          <img
            class="video-block__transcript-close-btn-blue"
            :src="`/themes/custom/education_applications_theme/dist/images/icon-light-close-blue.svg`"
            alt="toggle transcript"
          >
        </button>
        <div
          :id="`transcript-content-${id}`"
          class="video-block__transcript-content"
          :class="{'video-block__transcript-content--expanded': showTranscript}"
          :aria-labelledby="`transcript-button-${id}`"
          :aria-hidden="!showTranscript"
        >
          <div
            class="video-block__transcript-content-inner"
            :class="{'video-block__transcript-content-inner--expanded': showTranscript}"
          >
            <slot name="transcript" />
          </div>
        </div>
      </div>
    </div>
    <!-- Title & Description -->
    <div
      class="video-block__content"
      :class="{'video-block__content--expanded': showVideo}"
    >
      <div class="video-block__title-wrapper">
        <div
          class="video-block__title"
          :class="{'video-block__title--expanded': showVideo}"
          @click="toggleVideo"
        >
          {{ title }}
        </div>
      </div>
      <button
        v-if="showVideo"
        class="video-block__close c-button c-button--link"
        aria-label="Close video"
        @click="toggleVideo"
      >
        <img
          :src="`/themes/custom/education_applications_theme/dist/images/icon-light-close.svg`"
          class="video-block__close-btn"
          alt=""
        >
        <img
          :src="`/themes/custom/education_applications_theme/dist/images/icon-light-close-blue.svg`"
          class="video-block__close-btn-blue"
          alt=""
        >
      </button>
      <div
        v-if="!showVideo"
        class="video-block__description"
      >
        {{ description }}
      </div>
      <div class="video-block__content-footer">
        <!-- eslint-disable-next-line max-len -->
        <button
          v-if="!showVideo"
          class="
            video-block__watch-button
            c-button
            c-button--link
            c-button--bold
            c-button--uppercase
          "
          @click="toggleVideo"
        >
          watch now
        </button>
        <span class="video-block__duration">
          {{ duration }}m
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { PulseLoader } from 'vue-spinner/dist/vue-spinner.min';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'VideoBlock',
  components: {
    PulseLoader,
    FontAwesomeIcon
  },
  props: {
    /**
     * A unique number used to aid accessibility.
     */
    id: {
      type: Number,
      required: true,
    },
    /**
     * Title for the block
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Description for the block
     */
    description: {
      type: String,
      required: true,
    },
    /**
     * Video duration
     */
    duration: {
      type: String,
      required: true,
    },
    /**
     * Url for the video thumbnail image
     */
    thumbnailUrl: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Url for video to be embeded in an iframe
     */
    videoUrl: {
      type: String,
      required: true,
    },
    /**
     * Title for the video in the iframe, can be different to the block title
     */
    videoTitle: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      showVideo: false,
      showTranscript: false,
    };
  },
  mounted() {
  },
  methods: {
    /**
     * Used for hiding and showing the video iframe
     */
    toggleVideo() {
      this.showVideo = !this.showVideo;
    },
    /**
     * Used for expanding and collapsing the video transcript
     */
    toggleTranscript() {
      this.showTranscript = !this.showTranscript;
    },
  },
};
</script>

<style scoped lang="scss">
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-width: 347px;
$size-thumbnail-width-mobile: calc(100vw - 2rem);
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-height: 196px;
$size-thumbnail-height-mobile: calc(56.48vw - 1rem);
/* stylelint-disable-next-line unit-disallowed-list */
$border-radius-small: 5px;

.video-block {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @include education-breakpoint-mobile-down {
    flex-direction: column;
  }

  &--expanded {
    flex-direction: column-reverse;
  }

  &__thumbnail {
    @include reset-button();

    width: $size-thumbnail-width;
    margin-right: gap-size(1.22);
    position: relative;
    transition: opacity $transition-duration $transition-timing;

    @include hover-focus-active {
      opacity: 0.8;
    }

    @include focus-active {
      @include focus-outline;
    }

    @include education-breakpoint-mobile-down {
      width: 100%;
      margin-right: 0;
      margin-bottom: gap-size(0.8);
    }
  }

  &__thumbnail-container {
    position: relative;
    width: $size-thumbnail-width;
    height: $size-thumbnail-height;

    @include education-breakpoint-mobile-down() {
      width: $size-thumbnail-width-mobile;
      height: $size-thumbnail-height-mobile;
    }
  }

  &__thumbnail-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &__thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
    font-family: "object-fit: cover;";
  }

  &__thumbnail-icon {
    position: absolute;
    right: 0;
    bottom: 0;
    background-color: $color-blue;
    width: 2rem;
    height: 2rem;
    padding: gap-size(calc(1/3));

    @include text-small;

    color: $color-white;
  }

  &__player {
    width: 100%;
    position: relative;
  }

  &__player-wrapper {
    position: relative;
    padding: 56.25% 0 0 0;
    box-sizing: border-box;
    overflow: hidden;
    background: $color-xlight-grey-s2;
  }

  .v-spinner {
    position: absolute;
    top: calc(50% - 1.33rem);
    left: calc(50% - 3.33rem);

    .v-pulse {
      margin: gap-size(0.25);
    }
  }

  &__iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__close {
    display: flex;
    justify-content: flex-start;

    .video-block__close-btn-blue {
      display: none;
    }

    @include hover-focus-active {
      .video-block__close-btn {
        display: none;
      }

      .video-block__close-btn-blue {
        display: inline-block;
      }

      .video-block__transcript-close-btn {
        display: none;
      }

      .video-block__transcript-close-btn-blue {
        display: inline-block;
      }
    }

    @include focus-active {
      @include focus-outline;
    }
  }

  &__close-icon {
    font-size: $font-size-medium;
    margin-right: gap-size(calc(1/4));
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin: 0;

    @include education-breakpoint-mobile-down() {
      width: $size-thumbnail-width-mobile;
    }

    &--expanded {
      flex-wrap: initial;
      padding: gap-size(0.65) gap-size(0.7);
      box-sizing: border-box;
      background-color: $color-white;
      border: $border-width solid $color-xlight-grey-s6;
      border-top-right-radius: $border-radius-small;
      border-top-left-radius: $border-radius-small;
    }

    .video-block--expanded & {
      @include education-breakpoint-tablet-down() {
        width: 100%;
      }
    }
  }

  &__content-footer {
    display: flex;
    justify-content: space-between;
    width: 100%;

    .video-block--expanded & {
      display: none;
    }
  }

  &__watch-button {
    line-height: 100%;

    @include focus-active {
      @include focus-outline;
    }
  }

  &__duration {
    @include text-small;
  }

  &__title {
    @include heading-small-medium;

    display: inline-block;
    margin: gap-size(0.21) 0 gap-size(0.5) 0;

    &:hover {
      cursor: pointer;
    }

    @include focus-active {
      @include focus-outline;
    }

    @include education-breakpoint-tablet-down() {
      margin: 0 0 gap-size(0.5) 0;
    }

    &:not(:first-child) {
      margin-top: 0;
    }

    &--expanded {
      display: block;
      width: 100%;
      box-sizing: border-box;
      margin: 0;
      background: $color-white;
      pointer-events: none;
    }
  }

  &__title-wrapper {
    width: 100%;
  }

  &__watch {
    @include reset-button();

    min-width: 5rem;
    font-family: $font-family;

    @include text-small;

    font-weight: $font-weight-bold;
    line-height: 1;
    text-align: left;
    color: $color-blue;
    margin-left: gap-size(calc(1/4));

    @include hover-focus-active {
      color: $color-blue-button-dark;
    }
  }

  &__description {
    width: 100%;

    @include text-small;

    margin: 0 0 gap-size(1.39) 0;

    @include education-breakpoint-tablet-down {
      margin-bottom: gap-size(0.67);
    }
  }

  &__underline {
    width: gap-size(1);
    height: 0.3rem;
    background-color: $color-yellow;
  }

  &__transcript {
    width: 100%;
    box-sizing: border-box;
    border: $border-width solid $color-xlight-grey-s6;
    border-bottom-left-radius: $border-radius-small;
    border-bottom-right-radius: $border-radius-small;
    background-color: $color-white;

    @include education-breakpoint-tablet-down() {
      width: 100%;
    }
  }

  &__transcript-button {
    @include reset-button();

    position: relative;
    width: 100%;
    padding: gap-size(0.5) gap-size(0.7);
    box-sizing: border-box;
    height: 3.72rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include text-small;

    &--expanded {
      .video-block__transcript-close-btn-blue {
        display: none;
      }

      .video-block__transcript-close-btn,
      .video-block__transcript-close-btn-blue {
        transform: rotate(0deg);
      }
    }

    .video-block__transcript-icon-blue {
      display: none;
    }

    @include hover-focus-active {
      .video-block__transcript-icon-blue {
        display: inline-block;
      }

      .video-block__transcript-icon {
        display: none;
      }

      .video-block__transcript-label {
        color: $color-blue-s3;
      }

      .video-block__transcript-close-btn {
        display: none;
      }

      .video-block__transcript-close-btn-blue {
        display: inline-block;
      }
    }

    @include focus-active {
      @include focus-outline;
    }
  }

  &__transcript-close-btn,
  &__transcript-close-btn-blue {
    transform: rotate(45deg);
    transition: transform $transition-duration $transition-timing;
  }

  &__transcript-close-btn-blue {
    display: none;
  }

  &__transcript-label {
    display: flex;

    @include text-small;

    font-size: 0.78rem;
  }

  &__transcript-icon,
  &__transcript-icon-blue {
    margin-right: gap-size(0.55);
  }

  &__transcript-content {
    p {
      @include text-small;
    }

    height: 0;
    overflow: hidden;
    transition: height $transition-duration $transition-timing;

    &--expanded {
      height: auto;
      margin-bottom: 1rem;
      padding: gap-size(0.5) gap-size(1.67);

      @include education-breakpoint-tablet-down {
        padding: 0 gap-size(0.7);
      }

      .video-block__transcript-content-inner {
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
    }
  }

  &__transcript-content-inner {
    transition: $transition-expand;
    transform: translate3d(0, -1rem, 0);
    opacity: 0;
  }
}
</style>
